import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PageWrapper from "components/PageWrapper";
import {
  getActiveDistributor,
  getSearchDataApi,
  getSearchResponseApi,
} from "actions/distributorActions";
import { DISTRIBUTORS } from "constants/common";
import { resetDistributorData, setDistributorData } from "../redux/searchSlice";
import { addToCart } from "../redux/cartSlice";
import SearchPlaceholder from "components/SearchPlaceholder";
import SearchHistoryTable from "components/SearchHistoryTable";
import { getProductPriceSlabs, filterProductsWithSlab, getCurrencySymbol } from "utils/searchUtils";
import TableLoader from "components/skeleton/TableLoader";
import { Search, Info, Minus, Plus, Check } from "lucide-react";
import { useToast } from "hooks/use-toast";
import { useAuth } from "AuthContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { formatManufacturerName } from "utils/stringUtils";
import Spinner from "components/Spinner";

const MySwal = withReactContent(Swal);

const SearchPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { addToCart, cart } = useAuth();
  const search = useSelector((state) => state.search);

  const [all, setAll] = useState({
    loading: false,
    searchKey: "",
    distributors: [],
    uniqueId: "",
    checkedDistributors: false,
  });
  const [searchResponseLoading, setSearchResponseLoading] = useState(false);
  const [searchData, setSearchData] = useState({
    products: [],
    groupArray: [],
  });
  const { searchKey, distributors, loading, uniqueId, checkedDistributors } = all;
  const { products, groupArray } = searchData;
  const rollingRef = useRef();

  // In-page search bar. Submitting navigates to /search?key=<term>, which the
  // location effect below picks up to kick off the search (same route the
  // header search uses), so the existing flow is reused.
  const [query, setQuery] = useState("");

  const submitSearch = (e) => {
    if (e) e.preventDefault();
    const term = query.trim();
    if (!term) return;
    navigate(`/search?key=${encodeURIComponent(term)}`);
  };

  const handleDistributorResponse = (name, data) => {
    // console.log({ name, data });
    setAll((prev) => ({ ...prev, loading: true }));
    if (data.status) dispatch(setDistributorData({ name, data: data.data }));
  };

  const onLoad = async () => {
    setAll((prev) => ({ ...prev, loading: true }));
    if(distributors.length > 0)
      return;
    const distributorRes = await getActiveDistributor();
    // console.log({ distributorRes });

    const filteredDistributors = [];
    if (distributorRes.status)
      for (let i = 0; i < DISTRIBUTORS.length; i++) {
        if (distributorRes.data[DISTRIBUTORS[i].name]) filteredDistributors.push(DISTRIBUTORS[i]);
      }
    // console.log({ filteredDistributors });
    setAll((prev) => ({ ...prev, distributors: filteredDistributors, checkedDistributors: true }));
  };

  const funChangeQty = (type, index, groupIndex) => {
    // console.log({ type, index });
    const copyGroupData = [...groupArray];
    const copyProducts = [...copyGroupData[groupIndex].products];
    let product = copyProducts[index];
    let currentQty = product?.required_qty ? product?.required_qty : product.price.min_order || 1;
    // console.log({ currentQty });
    if (type === "decrease") {
      currentQty -= parseInt(product.price.multiple);
      if (currentQty <= 0) product.required_qty = product.price.multiple;
      else product.required_qty = currentQty;
    } else {
      product.required_qty = parseInt(currentQty) + parseInt(product.price.multiple);
    }

    copyProducts[index] = product;
    copyGroupData[groupIndex].products = copyProducts;

    setSearchData({ groupArray: copyGroupData, products: copyProducts });
  };

  const funManualQtyChange = (value, index, groupIndex) => {
    const copyGroupData = [...groupArray];
    const copyProducts = [...copyGroupData[groupIndex].products];
    const product = copyProducts[index];
    const parsed = parseInt(value);
    product.required_qty = isNaN(parsed) || parsed < 1 ? 1 : parsed;
    copyProducts[index] = product;
    copyGroupData[groupIndex].products = copyProducts;
    setSearchData({ groupArray: copyGroupData, products: copyProducts });
  };

  const handleDistributorData = () => {
    const allDistributorResults = Object.values(search);
    let productArray = [];

    for (let i = 0; i < allDistributorResults.length; i++)
      if (allDistributorResults[i]?.length > 0) productArray.push(...allDistributorResults[i]);

    const groupArray = groupProducts(productArray);

    productArray = productArray.filter((product) => product.availability > 0);
    setSearchData({ ...searchData, products: productArray, groupArray });
    // setAll({ ...all, loading: false });
  };

  const groupProducts = (productArray) => {
    const groups = [...new Set(productArray.map((item) => item.part_number))];
    // console.log({ groups, productArray });
    const groupArray = [];
    for (let j = 0; j < groups.length; j++) {
      const groupProducts = productArray.filter(
        (product) => product.part_number === groups[j] && product.availability > 0,
      );
      const filteredProducts = filterProductsWithSlab({
        products: groupProducts,
      });

      // console.log({ filteredProducts });

      const groupData = {
        name: groups[j],
        priceSlabs: getProductPriceSlabs(filteredProducts),
        products: filteredProducts,
      };
      groupArray.push(groupData);
    }

    // setGroupData(groupArray);
    return groupArray;
  };

  const handleAddToCart = (product) => {
    // console.log("SearchPage handleAddToCart", { product });
    const cartProduct = {
      ...product,
      required_qty: parseInt(product.required_qty) || parseInt(product.price.min_order),
      manufacturer: formatManufacturerName(product.manufacturer),
    };
    // console.log({ cartProduct });
    addToCart(cartProduct); // update the cart in the context
    MySwal.fire({
      title: "Success",
      html: `Product: <strong>${product.part_number}</strong> added to cart`,
      icon: "success",
    });
    // addToCart(product); // update the cart in the context
    // const quantity = product?.required_qty ?? product?.price?.min_order ?? 1;
    // dispatch(addToCart({ product, quantity }));
  };

  const applyResponse = (response) => {
      // if (cancelled) return { shouldContinue: false, hasData: false };
      const items = response?.data;
      if (!items) return { shouldContinue: false, hasData: false };

      console.log({response})

      const searchStatus = response?.data?.processing_status;
      
      let hasData = false;
      Object.keys(items).forEach((key) => {
        if (key !== "search") {
          const distributorItems = Array.isArray(items[key]) ? items[key] : [];
          if (distributorItems.length > 0) hasData = true;
          dispatch(setDistributorData({ name: key, data: distributorItems }));
        }
      });

      // Continue polling if status is 1 (processing), stop if status is 2 (complete)
      const shouldContinue = searchStatus == "processing";
      if(searchStatus === "completed") {
        setSearchResponseLoading(false);
      }
      
      return { shouldContinue, hasData };
    };

  const pollForSearchData = async (uniqueId, attemptCount = 0) => {
    const MAX_POLL_ATTEMPTS = 10;
    const POLL_INTERVAL_MS = 5000;

    if (attemptCount >= MAX_POLL_ATTEMPTS) {
      setSearchResponseLoading(false);
      return;
    }

    const response = await getSearchResponseApi({ uniqueId });
    if (response?.status) {
      const { shouldContinue, hasData } = applyResponse(response);
    

      // Continue polling if status is 1 (processing)
      if (shouldContinue) {
        rollingRef.current = setTimeout(() => {
          pollForSearchData(uniqueId, attemptCount + 1);
        }, POLL_INTERVAL_MS);
      } else {
        // Nothing left to wait for — drop the loader even if the response
        // carried no processing_status.
        setSearchResponseLoading(false);
      }
    } else {
      // Retry on failed response
      const shouldRetry = response?.data?.search?.status == "1";
      if (shouldRetry) {
        rollingRef.current = setTimeout(() => {
          pollForSearchData(uniqueId, attemptCount + 1);
        }, POLL_INTERVAL_MS);
      } else {
        setSearchResponseLoading(false);
      }
    }
  }

  const handleSearchPartNumber = async (key) => {
    setSearchResponseLoading(true);
    // Clear any existing polling
    if (rollingRef.current) {
      clearTimeout(rollingRef.current);
      rollingRef.current = null;
    }
    
    Object.keys(search).forEach((name) => dispatch(resetDistributorData({ name })));
    const distributorArray = distributors.map((distributor) => distributor.key);
    const response = await getSearchDataApi({ keyword: key, distributors: distributorArray });

    console.log({response})

    if (response.status && response?.unique_id) {
      const uid = response.unique_id;
      setAll((prev) => ({ ...prev, uniqueId: uid }));
      const params = new URLSearchParams(location.search);
      params.set("unique_id", uid);
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    
    } else {
      setSearchResponseLoading(false);
    }
  };


  useEffect(() => {
    if (searchKey) onLoad();
  }, [searchKey]);

  // Keep the search bar populated with the active term (e.g. when landing here
  // from the header search or a shared URL).
  useEffect(() => {
    setQuery(searchKey || "");
  }, [searchKey]);

  useEffect(() => {
    // Cleanup polling when searchKey changes
    return () => {
      if (rollingRef.current) {
        clearTimeout(rollingRef.current);
        rollingRef.current = null;
      }
    };
  }, [searchKey]);

  useEffect(() => {
    return () => {
      ["mouser", "digikey", "element14", "tme", "rutronik", "ti", "arrow"].forEach((name) => {
        dispatch(resetDistributorData({ name }));
      });
    };
  }, [dispatch]);

  useEffect(() => {
    if (distributors.length > 0 && searchKey && !uniqueId) {
      // handleSearchPartNumber();
    }
  }, [distributors, searchKey, uniqueId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const key = params.get("key");
    const uniqueIdFromUrl = params.get("unique_id");
    // Start loader immediately on new search URL to avoid "not found" flicker.

    setAll((prev) => ({
      ...prev,
      searchKey: key,
      uniqueId: uniqueIdFromUrl
    }));

    console.log({key, uniqueIdFromUrl, distributors})

    if(key && !uniqueIdFromUrl && distributors.length > 0)
      handleSearchPartNumber(key);

    if(key && uniqueIdFromUrl && distributors.length > 0) {
      // Reached either from a fresh search or from the search history: clear the
      // previous term's results so a re-opened search shows only its own data.
      Object.keys(search).forEach((name) => dispatch(resetDistributorData({ name })));
      setSearchResponseLoading(true);
      pollForSearchData(uniqueIdFromUrl, 0);
    }

  }, [location, distributors]);

  useEffect(() => {
    handleDistributorData();
  }, [search]);

  const hasAnyProducts =
    Array.isArray(groupArray) && groupArray.some((g) => g.products?.length > 0);
  const groupsWithProducts = hasAnyProducts ? groupArray.filter((g) => g.products?.length > 0) : [];

  return (
    <PageWrapper>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
            Search
          </h1>
          <span className="text-sm text-s360-fg3">Search for parts and components</span>
        </div>

        <form onSubmit={submitSearch} className="flex items-center gap-2 w-full max-w-[640px]">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-s360-fg3 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by part number…"
              className="w-full h-11 pl-10 pr-3 bg-white border border-s360-stroke1 border-b-2 rounded-md text-sm text-s360-fg1 outline-none transition-colors focus:border-s360-violet focus:border-b-s360-violet placeholder:text-s360-fg-disabled"
            />
          </div>
          <button
            type="submit"
            disabled={!query.trim() || searchResponseLoading}
            className="inline-flex items-center gap-1.5 h-11 px-5 rounded-md bg-s360-violet text-white text-sm font-semibold hover:bg-s360-violet-600 disabled:opacity-60 transition-colors"
          >
            <Search className="w-4 h-4" /> Search
          </button>
        </form>

        {searchResponseLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Spinner bare size={32} />
            <span className="text-s360-fg3 font-medium">Fetching search results…</span>
          </div>
        ) : checkedDistributors && distributors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
            <span className="w-14 h-14 rounded-full bg-s360-bg2 text-s360-violet inline-flex items-center justify-center">
              <Search className="w-6 h-6" />
            </span>
            <h3 className="text-base font-semibold text-s360-fg1">Connect a distributor to search</h3>
            <p className="text-sm text-s360-fg3 max-w-[360px]">
              Search pulls live pricing and stock from your connected distributors. You don't have
              any active distributor yet, so there are no sources to search.
            </p>
            <button
              type="button"
              onClick={() => navigate("/settings")}
              className="mt-1 inline-flex items-center justify-center h-9 px-4 rounded-md bg-s360-violet text-white text-sm font-semibold shadow-sm hover:bg-s360-violet-600 transition-colors"
            >
              Configure a distributor
            </button>
          </div>
        ) : !hasAnyProducts ? (
          <>
            {/* Only claim "not found" once a search actually ran; landing on
                /search with no term just shows the history below. */}
            {searchKey && (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                <span className="w-14 h-14 rounded-full bg-s360-bg2 text-s360-violet inline-flex items-center justify-center">
                  <Search className="w-6 h-6" />
                </span>
                <h3 className="text-base font-semibold text-s360-fg1">Part number not found</h3>
                <p className="text-sm text-s360-fg3 max-w-[360px]">
                  We couldn't find{" "}
                  <strong className="font-mono text-s360-fg1">{searchKey}</strong> across any
                  connected distributor. Double-check the part number and try again.
                </p>
              </div>
            )}
            <SearchHistoryTable />
          </>
        ) : (
          groupsWithProducts.map((group, groupIndex) => (
            <div key={group.name ?? groupIndex} className="flex flex-col gap-3">
              <h3 className="font-mono text-lg font-bold text-s360-violet">{group.name}</h3>
              <TooltipProvider delayDuration={300}>
                <div className="flex border border-s360-stroke1 rounded-md overflow-hidden shadow-sm bg-white">
                  {/* Left Section - Fixed Product Info */}
                  <div className="flex-shrink-0 flex flex-col">
                    <table className="border-collapse h-full">
                      <thead>
                        <tr>
                          {["Distributor", "Make", "SKU", "Stock"].map((h) => (
                            <th
                              key={h}
                              className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-white bg-s360-violet border-b border-r border-s360-violet-600 whitespace-nowrap"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {group.products.map((product, index) => (
                          <tr key={index} className="bg-white border-b border-s360-bg2">
                            <td className="p-3 whitespace-nowrap border-r border-s360-bg2 font-semibold text-center text-s360-fg1">
                              {product.api_partner.toUpperCase()}
                            </td>
                            <td className="p-3 whitespace-nowrap border-r border-s360-bg2 text-s360-fg2">
                              {formatManufacturerName(product.manufacturer)}
                            </td>
                            <td className="p-3 border-r border-s360-bg2 max-w-[280px]">
                              <div className="font-mono font-semibold text-[13px] text-s360-fg1">
                                {product.part_number}
                              </div>
                              <div className="text-xs text-s360-fg3">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="block truncate cursor-pointer">
                                      {product.description || "—"}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="top"
                                    className="max-w-md whitespace-normal break-words"
                                  >
                                    {product.description || "—"}
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </td>
                            <td className="p-3 whitespace-nowrap border-r border-s360-bg2 font-mono text-s360-success font-semibold tabular-nums">
                              {Number(product.availability).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Middle Section - Scrollable Price Slabs */}
                  <div className="flex-1 overflow-x-auto min-h-0 flex flex-col">
                    <table className="border-collapse min-w-full h-full">
                      <thead>
                        <tr>
                          {group.priceSlabs.length > 0 ? (
                            group.priceSlabs.map((slab, slabIndex) => (
                              <th
                                key={slabIndex}
                                className="text-center py-3 px-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-white bg-s360-violet border-b border-r border-s360-violet-600 whitespace-nowrap min-w-[80px]"
                              >
                                {slab.min_qty}
                              </th>
                            ))
                          ) : (
                            <th className="text-center py-3 px-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-white bg-s360-violet border-b border-r border-s360-violet-600 whitespace-nowrap">
                              No Price Data
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {group.products.map((product, index) => (
                          <tr key={index} className="bg-white border-b border-s360-bg2">
                            {group.priceSlabs.length > 0 ? (
                              group.priceSlabs.map((slab, slabIndex) => {
                                const findSlab = product.price?.price_list?.find(
                                  (list) => list.min_qty === slab.min_qty,
                                );
                                return (
                                  <td
                                    key={slabIndex}
                                    className="p-3 py-6 text-center whitespace-nowrap border-r border-s360-bg2 bg-s360-stripe"
                                  >
                                    {findSlab ? (
                                      <span className="font-mono font-semibold text-[13px] text-s360-fg1 tabular-nums">
                                        {getCurrencySymbol(findSlab.currency)}
                                        {findSlab.price}
                                      </span>
                                    ) : (
                                      <span className="text-s360-fg-disabled">-</span>
                                    )}
                                  </td>
                                );
                              })
                            ) : (
                              <td className="p-3 py-6 text-center whitespace-nowrap border-r border-s360-bg2">
                                <span className="text-s360-fg-disabled">-</span>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Right Section - Fixed Cart Actions */}
                  <div className="flex-shrink-0 flex flex-col">
                    <table className="border-collapse h-full">
                      <thead>
                        <tr>
                          <th className="text-center py-3 px-4 bg-s360-violet border-b border-s360-violet-600 whitespace-nowrap min-w-[200px]">
                            <span style={{ visibility: "hidden" }}>MOQ & MUL</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.products.map((product, index) => (
                          <tr key={index} className="bg-white border-b border-s360-bg2">
                            <td className="px-3 py-[19px] align-top">
                              <div className="flex items-center justify-between gap-2">
                                <div
                                  className="flex items-center text-s360-violet"
                                  title="MOQ & MUL"
                                >
                                  <Info className="w-3.5 h-3.5 mr-1.5" />
                                  <span className="text-xs font-medium text-s360-fg2">
                                    MOQ & MUL
                                  </span>
                                </div>
                                <div className="flex items-center border border-s360-stroke1 rounded-md overflow-hidden">
                                  <button
                                    className="px-2 py-1 hover:bg-s360-bg2 text-s360-fg2"
                                    onClick={() => funChangeQty("decrease", index, groupIndex)}
                                    aria-label="Decrease quantity"
                                  >
                                    <Minus className="w-3 h-3" />
                                  </button>
                                  <input
                                    type="number"
                                    min="1"
                                    value={product?.required_qty || product?.price?.min_order || 1}
                                    onChange={(e) =>
                                      funManualQtyChange(e.target.value, index, groupIndex)
                                    }
                                    className="w-14 text-center font-semibold border-x border-s360-stroke1 py-1 focus:outline-none text-sm tabular-nums"
                                  />
                                  <button
                                    className="px-2 py-1 hover:bg-s360-bg2 text-s360-fg2"
                                    onClick={() => funChangeQty("increase", index, groupIndex)}
                                    aria-label="Increase quantity"
                                  >
                                    <Plus className="w-3 h-3" />
                                  </button>
                                </div>
                                <button
                                  className="inline-flex items-center gap-1 bg-s360-violet hover:bg-s360-violet-600 text-white px-2.5 py-1.5 rounded-md font-semibold text-xs transition-colors whitespace-nowrap"
                                  onClick={() =>
                                    handleAddToCart({
                                      ...product,
                                      required_qty:
                                        product?.required_qty || product?.price?.min_order || 1,
                                    })
                                  }
                                >
                                  <Check className="w-3 h-3" /> Add to cart
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TooltipProvider>
            </div>
          ))
        )}
      </div>
    </PageWrapper>
  );
};

export default SearchPage;
